import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { reduxForm, Field, change } from 'redux-form';
import { connect } from 'react-redux';
import { SelectInput } from 'components/Common/Inputs';
import { ApiService } from 'services';
import { toastr } from 'react-redux-toastr';
import RadioTableGroup from 'components/Common/Inputs/RadioTableGroup';
import WithholdingStatementUploader from 'components/Common/WithholdingStatementUpload/WithholdingStatementUploader';

const hasWhdStatement = (value, values) => (values.witholdingStmnt && (value === 'undefined' || value === null));

class WithholdingStatementUploadForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    statementType: PropTypes.string,
    onFileUploadCancel: PropTypes.func.isRequired,
    onFileUploadError: PropTypes.func.isRequired,
    onFileUploadSuccess: PropTypes.func.isRequired,
    onStatementTypeChange: PropTypes.func.isRequired,
    onStatementFormatChange: PropTypes.func.isRequired,
    onFileDeleteSuccess: PropTypes.func,
    onFileDeleteError: PropTypes.func,
    formErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.any)]),
    disableToastr: PropTypes.bool,
    ddLabelText: PropTypes.string,
    existingStatements: PropTypes.any,
    disableConfirm: PropTypes.func.isRequired,
    singleColumnLayout: PropTypes.bool,
    statementFormat: PropTypes.string,
    accountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    legalEntityIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    settings: PropTypes.shape({
      showAiWithholdingStatements: PropTypes.bool,
    })
  };

  static defaultProps = {
    statementType: null,
    formErrors: [],
    disableToastr: false,
    onFileDeleteError: null,
    onFileDeleteSuccess: null,
    ddLabelText: 'Type',
    existingStatements: null,
    singleColumnLayout: true,
    statementFormat: 'standard',
    accountId: null,
    customerId: null,
    legalEntityIds: [],
    settings: {}
  };

  constructor(props) {
    super(props);

    this.state = {
      whdStatementType: props.statementType,
      whdStatement: null,
      whdStatementError: null,
      whdStatementUploading: false,
      whdStatementFormat: props.statementFormat,
      aiAllowed: true,
      aiOffMessage: null
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    const { whdStatementFormat } = this.state;
    dispatch(change('whdStmtUploadForm', 'statementFormat', whdStatementFormat));
    this.loadAiPermission();
  }

  componentDidUpdate(prevProps) {
    const { statementType, accountId, customerId, legalEntityIds } = this.props;
    const { whdStatementType } = this.state;
    const { statementFormat } = this.props;
    const { whdStatementFormat } = this.state;

    if (prevProps.accountId !== accountId || prevProps.customerId !== customerId || prevProps.legalEntityIds.join() !== legalEntityIds.join()) {
      this.loadAiPermission();
    }

    if (prevProps.statementType !== statementType && statementType !== whdStatementType && statementFormat !== whdStatementFormat) {
      this.resetStatement();
    }
  }

  onWhdStatementTypeChange(e, newValue) {
    const { onStatementTypeChange } = this.props;
    this.setState({ whdStatementType: newValue });
    if (onStatementTypeChange) {
      onStatementTypeChange(e, newValue);
    }
  }

  onWhdStatementFileUploadCancel(file) {
    const { onFileUploadCancel, disableConfirm } = this.props;

    if (onFileUploadCancel) {
      disableConfirm(false);
      onFileUploadCancel(file);
    }
  }

  onWhdStatementFileUploadError(file, response) {
    const { onFileUploadError, disableConfirm } = this.props;

    const fileDetails = {
      name: file.name,
      size: file.size,
      lastModified: file.lastModified
    };

    let whdError = {
      ...response.result,
      file: fileDetails
    };

    if (!response.result || (response.result && response.result.resultCode === 10)) { // Unlicensed
      whdError = null;
      toastr.error('', response.message);
    }

    this.setState({ whdStatement: null });
    this.setState({ whdStatementError: whdError });
    this.setState({ whdStatementUploading: false });

    if (onFileUploadError) {
      disableConfirm(false);
      onFileUploadError(file, response);
    }
  }

  onWhdStatementFileSending(file) {
    const { disableConfirm } = this.props;
    if (file) {
      disableConfirm(true);
      this.setState({ whdStatementError: null });
      this.setState({ whdStatementUploading: true });
    }
  }

  onWhdStatementFileUploadSuccess(file, response) {
    const { disableToastr, onFileUploadSuccess, disableConfirm } = this.props;
    const { whdStatementFormat } = this.state;
    const updatedResponse = { ...response, statementFormat: response?.statementFormat || whdStatementFormat };
    this.setState({ whdStatement: updatedResponse });
    this.setState({ whdStatementError: null });
    this.setState({ whdStatementUploading: false });

    if (!disableToastr) {
      if (updatedResponse && updatedResponse.file && updatedResponse.file.fileName) {
        disableConfirm(false);
        toastr.success('', `${ updatedResponse.file.fileName } has been uploaded`);
      } else {
        toastr.error('', `${ (file && file.name) || 'WHD Statement' } failed to upload`);
      }
    }

    if (onFileUploadSuccess) {
      onFileUploadSuccess(file, updatedResponse);
    }
  }

  onDeleteWhdStatement(file) {
    const { disableToastr, onFileDeleteError, onFileDeleteSuccess } = this.props;
    ApiService
      .deleteTempUpload(file.id)
      .then((result) => {
        if (result != null) {
          this.setState({ whdStatement: null });
        } else if (!disableToastr) {
          toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
        }
        if (onFileDeleteSuccess) {
          onFileDeleteSuccess(file);
        }
      })
      .catch((err) => {
        if (!disableToastr) {
          toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
        }
        if (onFileDeleteError) {
          onFileDeleteError(file, err);
        }
      });
  }

  onWhdStatementFormatChange(e, newValue) {
    const { onStatementFormatChange } = this.props;
    this.setState({
      whdStatementFormat: newValue,
    });
    if (onStatementFormatChange) {
      onStatementFormatChange(e, newValue);
    }
  }

  loadAiPermission() {
    const { accountId, customerId, legalEntityIds, settings } = this.props;

    if (!settings.showAiWithholdingStatements) {
      return;
    }

    ApiService.getAiPermission(accountId, customerId, legalEntityIds)
      .then((response) => {
        const aiAllowed = response?.data?.allowed !== false;
        this.setState({
          aiAllowed,
          aiOffMessage: aiAllowed ? null : response.data.message
        }, () => {
          if (!aiAllowed) {
            this.forceStandardFormat();
          }
        });
      })
      .catch(() => {
        this.setState({ aiAllowed: true, aiOffMessage: null });
      });
  }

  forceStandardFormat() {
    const { dispatch } = this.props;

    this.onWhdStatementFormatChange(null, 'standard');
    dispatch(change('whdStmtUploadForm', 'statementFormat', 'standard'));
  }

  resetStatement() {
    const { dispatch, statementType, statementFormat } = this.props;

    this.setState({
      whdStatementType: statementType,
      whdStatementFormat: statementFormat,
      whdStatement: null
    }, () => {
      dispatch(change('whdStmtUploadForm', 'statementType', statementType));
      dispatch(change('whdStmtUploadForm', 'statementFormat', statementFormat));
    });
  }

  isPooledStatement() {
    const { whdStatement } = this.state;
    return whdStatement && whdStatement.isValid && whdStatement.file && (whdStatement.file.type === 'QiPooled' || whdStatement.file.type === 'NqiPooled' || whdStatement.file.type === 'PooledWhdStatement');
  }

  fieldHasErrors(field) {
    const { formErrors } = this.props;
    return formErrors != null && formErrors.filter(error => error.field === field).length > 0;
  }

  renderAiOffFormatOptions() {
    const { aiOffMessage } = this.state;

    return (<React.Fragment>
      <label className="form-radio">
        <input type="radio" name="statementFormat" id="statementFormatStandard" value="standard" checked readOnly />
        <i className="form-icon" /> Standard
      </label>
      <label className="form-radio">
        <input type="radio" name="statementFormat" id="statementFormatNonStandard" value="nonstandard" disabled aria-describedby="ai-off-reason" />
        <i className="form-icon" /> Non Standard
      </label>
      <p id="ai-off-reason" role="note" className="mb-0">{ aiOffMessage }</p>
    </React.Fragment>);
  }

  render() {
    const {
      ddLabelText,
      existingStatements,
      singleColumnLayout,
      settings
    } = this.props;
    const {
      whdStatement,
      whdStatementError,
      whdStatementType,
      whdStatementUploading,
      whdStatementFormat,
      aiAllowed
    } = this.state;

    const whdStatementFormatTextNonStandard = 'Non Standard';
    const whdStatementFormatTextStandard = 'Standard';
    let whdStatementFormatText = '';
    let costMessage = '';
    if (whdStatementFormat === 'nonstandard') {
      whdStatementFormatText = whdStatementFormatTextNonStandard;
      costMessage = 'Selecting statement format \'Non Standard\' will run the statement through AI to be read. Doing so may come at an additional cost';
    }
    if (whdStatementFormat === 'standard') {
      whdStatementFormatText = whdStatementFormatTextStandard;
    }
    return (<React.Fragment>
      { /* WHD Statement Upload --> */ }
      { existingStatements != null && existingStatements.length > 0 && <div className="form-group">
        <div className="col-12">
          <div className="card card-error">
            <div className="card-header">
              <h2>Existing WHD Statement{ existingStatements.length > 1 && <React.Fragment>s</React.Fragment> } Found</h2>
            </div>
            <div className="card-body">
              <p>Cannot upload new withholding statement as one is already present for the account{ existingStatements.length > 1 && <React.Fragment>s listed</React.Fragment> } below.</p>
              <div className="reasons-for-rejection-list">
                <ul className="mt-0">
                  { existingStatements.map(x => <li>
                    <strong>Account: </strong>{ x.account.accountNumber } - { x.account.accountName }<br />
                    <strong>Intermediary</strong>: { x.withholdingStatement.intermediaryName }, { x.withholdingStatement.intermediaryCountry }
                  </li>) }
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>}
      { (existingStatements == null || existingStatements.length === 0) && <div className="form-group">
        <div className={ ` ${ singleColumnLayout ? 'col-6 col-sm-12 pr-0' : '' } ` }>
          <div className="form-group">
            <div className={ ` ${ singleColumnLayout ? 'col-2 col-sm-12 pr-2' : '' } ` }>
              <label htmlFor="whd-statement-type" className="form-label">{ ddLabelText }</label>
            </div>
            <div className={ ` ${ singleColumnLayout ? 'col-10 col-sm-12 pr-2' : '' } ` }>
              <Field
                id="whd-statement-type"
                name="statementType"
                component={ SelectInput }
                className={ `form-select ${ this.fieldHasErrors('whdStmtType') ? 'is-error' : '' }` }
                validate={ hasWhdStatement }
                value={ whdStatementType ?? '' }
                disabled={ whdStatementUploading || !!whdStatement }
                onChange={ (e, newValue) => this.onWhdStatementTypeChange(e, newValue) }
              >
                <option value="">Choose a statement type</option>
                <React.Fragment>
                  <option value="altwhdstatement">Alt Statement</option>
                  <option value="detailedwhdstatement">Detailed Statement</option>
                  <option value="simplewhdstatement">Simple Statement</option>
                  <option value="pooledwhdstatement">Pooled Statement</option>
                </React.Fragment>
              </Field>
            </div>
          </div>
          { settings.showAiWithholdingStatements && <div className="form-group">
            <div className={ ` ${ singleColumnLayout ? 'col-2 col-sm-12 pr-2' : '' } ` }>
              <label htmlFor="statementFormat" className="form-label">Format</label>
            </div>
            <div className={ ` ${ singleColumnLayout ? 'col-10 col-sm-12 pr-2' : '' } ` }>
              { !whdStatementUploading && !whdStatement && aiAllowed && <RadioTableGroup
                name="statementFormat"
                value={ whdStatementFormat ?? '' }
                items={ [[{ text: whdStatementFormatTextStandard, value: 'standard' }, { text: whdStatementFormatTextNonStandard, value: 'nonstandard' }]] }
                preventReset
                onChange={ (e, newVal, prevVal) => this.onWhdStatementFormatChange(e, newVal, prevVal) }
                disabled={ whdStatementUploading || !!whdStatement }
                className="nowrap"
              /> }
              { !whdStatementUploading && !whdStatement && !aiAllowed && this.renderAiOffFormatOptions() }
              { (whdStatementUploading || !!whdStatement) && <label>{ whdStatementFormatText }</label> }
            </div>
          </div> }
        </div>
        { whdStatementType !== '' && whdStatementType != null && whdStatementFormat !== '' && whdStatementFormat != null && <div className={ ` ${ singleColumnLayout ? 'col-6 col-sm-12' : '' } ` }>
          <div className="form-group columns">
            { whdStatement == null && <WithholdingStatementUploader
              statementType={ whdStatementType }
              statementFormat={ aiAllowed ? whdStatementFormat : 'standard' }
              onFileSending={ file => this.onWhdStatementFileSending(file) }
              onFileUploadCancel={ file => this.onWhdStatementFileUploadCancel(file) }
              onFileUploadSuccess={ (file, response) => this.onWhdStatementFileUploadSuccess(file, response) }
              onFileUploadError={ (file, error) => this.onWhdStatementFileUploadError(file, error) }
            /> }
            { whdStatement != null && <div className="col-4 mt-2">
              <label htmlFor="pdf-form-file" className="form-label">WHD Stmt</label>
            </div> }
            { whdStatement != null && <div className="col-8 mt-2 whd-file-row-inline">
              <span className="whd-file-name ellipsis">{ whdStatement.file.fileName }</span>
              <button
                className="btn btn-sm btn-danger text-right"
                type="button"
                title="Delete WHD statement"
                onClick={ () => this.onDeleteWhdStatement(whdStatement.file) }
              >
                <span className="icon icon-delete" />
              </button>
            </div> }
          </div>
        </div> }
        { whdStatement == null && <div className="col-12 pt-2 whd-statement-validation">
          <p className="mt-1rem">{ costMessage }</p>
        </div> }
        { this.isPooledStatement(whdStatement) && <div className="col-12 pt-2 whd-statement-validation">
          <div className="card card-success">
            <div className="card-header">
              <h2>Withholding Statement Validation Successful</h2>
            </div>
            <div className="card-body">
              <p>Intermediary: <strong>{ whdStatement.statement.intermediaryName }</strong></p>
              <div>
                <p>Accounts:</p>
                <ul className="mt-0">
                  { whdStatement.statement.listOfAccounts && whdStatement.statement.listOfAccounts.map((a, i) => <li key={ (a && `${ a.accountNumber }-${ a.accountName }`) || i }>
                    { a.accountName }
                  </li>) }
                  { whdStatement.statement.accounts && whdStatement.statement.accounts.map((a, i) => <li key={ (a && `${ a.accountNumber }-${ a.accountName }`) || i }>
                    { a.accountName }
                  </li>) }
                </ul>
              </div>
            </div>
          </div>
        </div> }
        { whdStatementError != null && !whdStatementError.isValid && <div className="col-12 pt-2 whd-statement-validation">
          <div className="card card-error">
            <div className="card-header">
              <h2>WHD Statement Validation Failed</h2>
              { whdStatementError.file && <p><strong>File: </strong>{ whdStatementError.file.name } ({ whdStatementError.file.size / 1024 }kb)</p> }
            </div>
            <div className="card-body">
              <div className="reasons-for-rejection-list"><ul>
                { whdStatementError.validationResults && whdStatementError.validationResults.map(item => <li key={ `${ item.identifier ?? '' }-${ item.code }` }>
                  <em>{ item.codeDescription.replace('The withholding statement has failed validation. ', '') }</em>&nbsp;(<strong>{ item.code }</strong>)
                </li>) }
                { (!whdStatementError.validationResults || whdStatementError.validationResults.length === 0) && <li>
                  <em>The withholding statement failed to upload, but no error details were returned</em>
                </li> }
              </ul></div>
            </div>
          </div>
        </div> }
      </div> }
    </React.Fragment>);
  }
}

const WhdStmtUploadReduxForm = reduxForm({
  form: 'whdStmtUploadForm',
})(WithholdingStatementUploadForm);

const mapStateToProps = (state, ownProps) => ({
  settings: state.auth && state.auth.settings ? state.auth.settings : {},
  initialValues: { statementFormat: ownProps.statementFormat }
});

const mapDispatchToProps = dispatch => ({ dispatch });

export const WithholdingStatementUpload = connect(
  mapStateToProps,
  mapDispatchToProps
)(WhdStmtUploadReduxForm);

export default WithholdingStatementUpload;
